import {
  Bot,
  CircleCheckBig,
  Cloud,
  Cpu,
  DollarSign,
  KeyRound,
  Palette,
  Server,
  SlidersHorizontal,
  UserRound,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { DESTINATIONS_BY_ID } from "@/app/destinations";
import { CAPTURE_BY_ID, SETTINGS_BY_ID } from "@/app/surfaces";
import { useUiMode } from "@/lib/uiMode";
import { Placeholder } from "@/components/Placeholder";
import { AccountSettings } from "@/pages/AccountSettings";
import { ModeSettings } from "@/pages/ModeSettings";
import { AdvancedSettings } from "@/pages/AdvancedSettings";
import { EngineSettings } from "@/pages/EngineSettings";
import { ProviderSettings } from "@/pages/ProviderSettings";
import { SetupSettings } from "@/pages/SetupSettings";
import { ThemePicker } from "@/pages/ThemePicker";
import { UploadSourcePage, WriteNotePage } from "@/pages/UploadSource";
import { CaptureSettings } from "@/pages/CaptureSettings";
import { WorkerSettings } from "@/pages/WorkerSettings";
import { AgentSettings } from "@/pages/AgentSettings";
import { CostsSettings } from "@/pages/CostsSettings";

/**
 * One component serves every rail destination without a page of its own, because those
 * differ only in their descriptor. A room that is filled gets a real page in its route
 * instead; nothing here is a base class anybody has to inherit from.
 */
export function DestinationPage({ id }: { id: string }) {
  const destination = DESTINATIONS_BY_ID.get(id);
  if (!destination) return null;

  return (
    <Placeholder
      icon={destination.icon}
      title={destination.label}
      summary={destination.summary}
      milestone={destination.milestone}
    />
  );
}

export function CapturePage() {
  const { kind } = useParams<{ kind: string }>();
  const capture = kind ? CAPTURE_BY_ID.get(kind) : undefined;

  if (!capture) return <NotFound />;

  // Audio and documents upload a file (CAP-1/CAP-2/CAP-3); the note route (CAP-7) writes
  // its text straight in and has no file, so it is its own page.
  if (capture.upload) return <UploadSourcePage capture={capture} />;
  if (capture.id === "note") return <WriteNotePage capture={capture} />;

  return (
    <Placeholder
      icon={capture.icon}
      title={capture.label}
      summary={capture.summary}
      milestone={capture.milestone}
    />
  );
}

export function SettingsPage() {
  const { section: sectionId } = useParams<{ section: string }>();
  const mode = useUiMode();

  // Simple mode opens on Mode rather than Setup: Setup is a checklist about getting a
  // pipeline running, which is the one thing this surface's reader was promised they would
  // never have to think about (UI-16).
  const fallback = mode === "simple" ? "mode" : "setup";
  const section = sectionId ? SETTINGS_BY_ID.get(sectionId) : SETTINGS_BY_ID.get(fallback);

  if (!section) return <NotFound />;

  // UI-16 reduces Settings to four sections. The other five keep their addresses — a link
  // into one must not break for the mode somebody happens to be in — and answer with which
  // section it is and the way to it, exactly as the phone app answers for a desktop-only
  // screen (M12). Improvising a reduced version of Engines would be worse than saying so.
  if (mode === "simple" && !section.simple) {
    return <FullModeOnly label={section.label} summary={section.summary} />;
  }

  if (section.id === "mode") {
    return <ModeSettings summary={section.summary} />;
  }

  // Themes is the one section M1.2 actually delivers — UI-17 is finished here, so
  // showing a placeholder for it would be a lie in the other direction.
  if (section.id === "themes") {
    return <ThemePicker summary={section.summary} />;
  }

  // UI-13's four, all finished in M3.4. Setup is the one the milestone calls the real
  // deliverable: it is what tells the owner why an end-to-end run is not happening.
  if (section.id === "setup") {
    return <SetupSettings summary={section.summary} />;
  }

  if (section.id === "account") {
    return <AccountSettings summary={section.summary} />;
  }

  if (section.id === "advanced") {
    return <AdvancedSettings summary={section.summary} />;
  }

  // Worker: M2.1 built the token half (SEC-3) and M3.4 completed the rest (UI-13).
  if (section.id === "worker") {
    return <WorkerSettings summary={section.summary} />;
  }

  // CAP-5 — the PCs that file recordings (M11.2). Its own screen rather than a row on
  // the Worker one, because they are different machines doing different jobs.
  if (section.id === "capture") {
    return <CaptureSettings summary={section.summary} />;
  }

  // UI-14's two sections, both finished in M3.3. What is not finished is the set of
  // things the engines can be pointed at, and the screens say so themselves rather
  // than being placeholders.
  if (section.id === "engines") {
    return <EngineSettings summary={section.summary} />;
  }

  if (section.id === "providers") {
    return <ProviderSettings summary={section.summary} />;
  }

  // UI-22 (M18.2) — the Costs section, over ARCH-13's cloud spend ledger.
  if (section.id === "costs") {
    return <CostsSettings summary={section.summary} />;
  }

  // SEC-7 — the agents this account has connected, and the one way to disconnect one (M9.3).
  if (section.id === "agents") {
    return <AgentSettings summary={section.summary} />;
  }

  return (
    <Placeholder
      icon={SettingsIconFor(section.id)}
      title={section.label}
      summary={section.summary}
      milestone={section.milestone}
    />
  );
}

/**
 * A settings section simple mode does not draw, named rather than hidden.
 *
 * The address resolves, because a shared link and a bookmark must not break for the mode
 * the reader is in — the same rule M12 settled for the phone app, and the same shape of
 * answer: what this is, why it is not here, and one way to reach it.
 */
function FullModeOnly({ label, summary }: { label: string; summary: string }) {
  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-3)] px-[var(--space-8)] py-[var(--space-8)]">
      <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        {label}
      </h1>
      <p className="text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      <p className="max-w-[56ch] text-[length:var(--text-sm)] text-[var(--text-secondary)] [text-wrap:pretty]">
        This is part of full mode, which is where the pipeline is set up and tuned. Nothing here is
        needed to add a source, answer a question or read your world view.
      </p>
      <Link
        to="/settings/mode"
        className="mt-1 inline-flex w-fit items-center rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)]"
      >
        Switch to full mode
      </Link>
    </div>
  );
}

function NotFound() {
  return (
    <div className="px-[var(--space-8)] py-[var(--space-12)] text-center">
      <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
        There is nothing at this address
      </p>
      <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        The link may be from a later milestone.
      </p>
    </div>
  );
}

function SettingsIconFor(id: string): LucideIcon {
  const icons: Record<string, LucideIcon> = {
    setup: CircleCheckBig,
    mode: SlidersHorizontal,
    account: UserRound,
    engines: Cpu,
    providers: Cloud,
    costs: DollarSign,
    worker: Server,
    capture: KeyRound,
    agents: Bot,
    themes: Palette,
    advanced: Wrench,
  };

  return icons[id] ?? Wrench;
}
